/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: status (green/amber/red) classification.
// Source: cardiac_report_v0_5_6_dev_2.html lines 2799-2830 (sysDia status),
// 3284-3326 (marker status, effective color), 3356-3371 (cardiacCycleTimingColorClass),
// 3587-3591 (sysDiaDurationColorClass), 4121-4196 (getColorClass),
// 4729-4741 (getDetailedZone), 6687-6704 (getStatusLabel).
// Deviation: currentHeartRateBpm() global -> explicit hrBpm argument.

import { UNIDIRECTIONAL_ZONES, BIDIRECTIONAL_ZONES } from "./constants"
import { parseNormalRangeSpec, parseSdObservedRatio, parseSysDiaDurationObserved } from "./parse"
import {
  cardiacCycleRatioZonesForHeartRate,
  expectedSysDiaRangesForHeartRate,
  expectedDiaRangeForHeartRate,
} from "./zones"
import {
  heartRateRangeOf,
  isCardiacCycleTimingFeature,
  isHeartRateFeature,
  isSysDiaDurationFeature,
  isRhythmRegularityFeature,
} from "./features"
import {
  rhythmRegularityColorClass,
  rhythmRegularityPatternLabel,
  rhythmRegularityStatusFromPattern,
} from "./timing-pattern"

/** The worst of several grades: red over amber over grey over green. */
export function worstStatus(statuses) {
  if (statuses.includes("red")) return "red"
  if (statuses.includes("amber")) return "amber"
  if (statuses.includes("grey")) return "grey"
  return "green"
}

// A cycle-timing range ("1 : 1.6–1.9") is graded at its midpoint, as
// upstream grades it (parseSdObservedRatio reads a range so); the per-
// recording grades are the card's stored status (timing-sources.js).
export function cardiacCycleTimingColorClass(observed, fallbackStatus, hrBpm) {
  const ratio = parseSdObservedRatio(observed)
  if (Number.isFinite(ratio)) {
    const zones = cardiacCycleRatioZonesForHeartRate(hrBpm)
    if (ratio >= zones.normalLow && ratio <= zones.normalHigh) return "green"
    if ((ratio >= zones.redLow && ratio < zones.normalLow) || (ratio > zones.normalHigh && ratio <= zones.redHigh))
      return "amber"
    return "red"
  }
  if (["green", "amber", "red", "grey"].includes(fallbackStatus)) return fallbackStatus
  const statusText = String(fallbackStatus || "").toLowerCase()
  if (statusText.includes("normal")) return "green"
  if (statusText.includes("borderline") || statusText.includes("variable")) return "amber"
  if (statusText.includes("outside") || statusText.includes("elevated") || statusText.includes("low")) return "red"
  return "green"
}

export function sysDiaDurationStatusFromValues(sysMs, diaMs, hr, fallbackStatus) {
  if (!Number.isFinite(diaMs) && !Number.isFinite(sysMs)) return fallbackStatus || "green"
  const ranges = expectedSysDiaRangesForHeartRate(hr)
  let worst = "green"
  if (Number.isFinite(diaMs)) {
    const width = Math.max(100, ranges.diaMax - ranges.diaMin)
    if (diaMs < ranges.diaMin - width * 0.25 || diaMs > ranges.diaMax + width * 0.25) worst = "red"
    else if (diaMs < ranges.diaMin || diaMs > ranges.diaMax) worst = "amber"
  }
  if (Number.isFinite(sysMs)) {
    const width = Math.max(80, ranges.sysMax - ranges.sysMin)
    const nearLow = ranges.sysMin - width * 0.25
    const nearHigh = ranges.sysMax + width * 0.25
    const redLow = ranges.sysMin - width * 0.6
    const redHigh = ranges.sysMax + width * 0.6
    const sysStatus =
      sysMs < redLow || sysMs > redHigh ? "red" : sysMs < nearLow || sysMs > nearHigh ? "amber" : "green"
    if (sysStatus === "red") worst = "red"
    else if (sysStatus === "amber" && worst === "green") worst = "amber"
  }
  return worst
}

export function sysDiaDurationStatusFromDia(diaMs, hr, fallbackStatus) {
  if (!Number.isFinite(diaMs)) return fallbackStatus || "green"
  const range = expectedDiaRangeForHeartRate(hr)
  if (diaMs >= range.min && diaMs <= range.max) return "green"
  const width = Math.max(100, range.max - range.min)
  if (diaMs >= range.min - width * 0.25 && diaMs <= range.max + width * 0.25) return "amber"
  return "red"
}

// A phase given as a range is graded at its midpoint, as upstream grades it
// (parseSysDiaDurationObserved reads a range so); the per-recording grades
// are the card's stored status (timing-sources.js).
export function sysDiaDurationColorClass(observed, fallbackStatus, hrBpm) {
  const parsed = parseSysDiaDurationObserved(observed)
  return sysDiaDurationStatusFromValues(parsed.sysMs, parsed.diaMs, hrBpm, fallbackStatus)
}

export function getColorClass(featureName, observed, normal, status, scaleType, hrBpm) {
  if (isRhythmRegularityFeature(featureName)) {
    return rhythmRegularityColorClass(observed, status, hrBpm)
  }
  if (isCardiacCycleTimingFeature(featureName)) {
    return cardiacCycleTimingColorClass(observed, status, hrBpm)
  }
  if (isSysDiaDurationFeature(featureName)) {
    return sysDiaDurationColorClass(observed, status, hrBpm)
  }

  // GUARD: If normal range is empty and no status, default to green
  if (!normal || String(normal).trim() === "") {
    return status || "green"
  }

  const rangeSpec = parseNormalRangeSpec(normal, String(observed).includes("%") ? "percentage" : "")
  if (!rangeSpec) return status || "green"
  const normMin = rangeSpec.min
  const normMax = rangeSpec.max
  const isUnidirectional = scaleType === "unidirectional"

  const observedCleaned = String(observed)
    .replace(/[%<>≤≥ms:bp]/g, "")
    .trim()

  if (!isCardiacCycleTimingFeature(featureName) && observedCleaned.includes("–")) {
    const observedParts = observedCleaned.split("–").map((p) => parseFloat(p.trim()))
    const obsMin = observedParts[0]
    const obsMax = observedParts[1]
    // A heart-rate range is graded at each end (v0.5.8): the ends agree, or
    // the range crosses reference categories and is borderline as a whole
    if (isHeartRateFeature(featureName) && Number.isFinite(obsMin) && Number.isFinite(obsMax)) {
      return heartRateRangeGrades({ min: obsMin, max: obsMax }, normal, scaleType).status
    }

    if (obsMin >= normMin && obsMax <= normMax) {
      return "green"
    }
    if (
      (obsMin < normMin && obsMax >= normMin && obsMax <= normMax) ||
      (obsMin >= normMin && obsMin <= normMax && obsMax > normMax)
    ) {
      return "amber"
    }
    return "red"
  }

  const obsValue = isCardiacCycleTimingFeature(featureName)
    ? parseSdObservedRatio(observed)
    : parseFloat(observedCleaned)
  if (!Number.isFinite(obsValue)) return status || "green"

  if (rangeSpec.kind === "lower") {
    if (obsValue >= normMin) return "green"
    const rangeForLower = normMax - normMin
    if (rangeForLower > 0 && obsValue >= normMin - rangeForLower * 0.2) return "amber"
    return "red"
  }

  if (obsValue >= normMin && obsValue <= normMax) {
    return "green"
  }

  const range = normMax - normMin
  if (isUnidirectional) {
    // For unidirectional: only "high" is problematic, borderline is 20% above max
    if (obsValue > normMax && obsValue < normMax + range * 0.2) {
      return "amber"
    }
    if (obsValue >= normMax + range * 0.2) {
      return "red"
    }
  } else {
    // For bidirectional: both "low" and "high" are problematic
    if (obsValue < normMin && obsValue > normMin - range * 0.2) {
      return "amber"
    }
    if (obsValue > normMax && obsValue < normMax + range * 0.2) {
      return "amber"
    }
    return "red"
  }
  return "green"
}

export function getStatusFromMarkerPosition(markerPos, scaleType, feature, hrBpm) {
  if (isRhythmRegularityFeature(feature)) {
    if (markerPos <= UNIDIRECTIONAL_ZONES.greenEnd) return "green"
    if (markerPos <= UNIDIRECTIONAL_ZONES.amberEnd) return "amber"
    return "red"
  }
  if (isCardiacCycleTimingFeature(feature)) {
    // Resolved via marker module to avoid a cycle; callers use statusFromMarkerWithRatio.
    return statusFromMarkerRatioHook(markerPos, hrBpm)
  }
  if (scaleType === "unidirectional") {
    if (markerPos <= UNIDIRECTIONAL_ZONES.greenEnd) {
      return "green"
    } else if (markerPos <= UNIDIRECTIONAL_ZONES.amberEnd) {
      return "amber"
    } else {
      return "red"
    }
  } else {
    if (markerPos >= BIDIRECTIONAL_ZONES.amberLowEnd && markerPos <= BIDIRECTIONAL_ZONES.greenEnd) {
      return "green"
    } else if (
      (markerPos >= BIDIRECTIONAL_ZONES.redLowEnd && markerPos < BIDIRECTIONAL_ZONES.amberLowEnd) ||
      (markerPos > BIDIRECTIONAL_ZONES.greenEnd && markerPos <= BIDIRECTIONAL_ZONES.amberHighEnd)
    ) {
      return "amber"
    } else {
      return "red"
    }
  }
}

// Small seam: original getStatusFromMarkerPosition called cardiacCycleRatioFromMarkerPosition
// + formatSystoleDiastoleDisplay from the marker module (circular import in module form).
// The marker module injects the hook at load time; behavior is identical.
let statusFromMarkerRatioHook = function (markerPos, hrBpm) {
  return "green"
}
export function __setCardiacMarkerStatusHook(fn) {
  statusFromMarkerRatioHook = fn
}

/**
 * How a heart-rate range grades: each end on its own, and as a whole — the
 * ends' shared grade, or amber when the range crosses reference categories
 * ("mixed"). Heart Report v0.5.8 at cardiac-panel-v4 git 31d729a.
 */
export function heartRateRangeGrades(range, normalRange, scaleType) {
  const gradeOf = (bpm) => getColorClass("Heart Rate", `${bpm} bpm`, normalRange, null, scaleType)
  const minClass = gradeOf(range.min)
  const maxClass = gradeOf(range.max)
  const mixed = minClass !== maxClass
  return { minClass, maxClass, mixed, status: mixed ? "amber" : minClass }
}

// A SYS:DIA Ratio or SYS:DIA Duration card whose value spans the recordings
// it includes: its marker sits at a representative median, so the grade
// comes from the range's ends, not from the marker.
// The grade a card shows (and its patient copy and documents read): a
// heart-rate range from its ends; once the reviewer has touched a card, the
// marker (for a SYS:DIA Ratio range that is the representative ratio read
// back through its one-decimal display text, upstream's own quirk); else
// the observed text — a SYS:DIA range at its midpoint. Upstream's order.
export function effectiveFeatureColorClass(feature, hrBpm) {
  if (!feature) return "green"
  // A heart-rate range is graded from its ends, whoever set them
  const range = heartRateRangeOf(feature)
  if (range) return heartRateRangeGrades(range, feature.normalRange, feature.scaleType).status
  let colorClass = null
  if (feature.reviewTouched && Number.isFinite(Number(feature.markerPosition))) {
    colorClass = getStatusFromMarkerPosition(Number(feature.markerPosition), feature.scaleType, feature, hrBpm)
    // A reviewer adjustment is authoritative: the imported timing-pattern
    // metrics must not override the marker they just moved.
    return colorClass || "green"
  }
  if (!colorClass) {
    colorClass = getColorClass(
      feature.name,
      feature.observed,
      feature.normalRange,
      feature.status,
      feature.scaleType,
      hrBpm
    )
  }
  if (isRhythmRegularityFeature(feature)) {
    const rhythmPattern = rhythmRegularityPatternLabel(feature, colorClass)
    colorClass = rhythmRegularityStatusFromPattern(rhythmPattern, colorClass)
  }
  return colorClass || "green"
}

export function getDetailedZone(markerPos, scaleType) {
  if (scaleType === "unidirectional") {
    if (markerPos <= UNIDIRECTIONAL_ZONES.greenEnd) return "green"
    if (markerPos <= UNIDIRECTIONAL_ZONES.amberEnd) return "amber"
    return "red"
  }
  // Bidirectional
  if (markerPos < BIDIRECTIONAL_ZONES.redLowEnd) return "red-low"
  if (markerPos < BIDIRECTIONAL_ZONES.amberLowEnd) return "amber-low"
  if (markerPos <= BIDIRECTIONAL_ZONES.greenEnd) return "green"
  if (markerPos <= BIDIRECTIONAL_ZONES.amberHighEnd) return "amber-high"
  return "red-high"
}

export function getStatusLabel(feature, colorClass) {
  const range = heartRateRangeOf(feature)
  if (range && heartRateRangeGrades(range, feature.normalRange, feature.scaleType).mixed) return "Mixed range"
  if (isRhythmRegularityFeature(feature)) {
    const pattern = rhythmRegularityPatternLabel(feature, colorClass)
    if (pattern === "Expected PCG timing variability") return "Normal"
    if (pattern) return pattern
    if (colorClass === "green") return "Normal"
    if (colorClass === "amber") return "Moderate PCG timing variability"
    if (colorClass === "red") return "High PCG timing variability"
  }
  if (colorClass === "green") return "Normal"
  if (colorClass === "amber") return "Borderline"
  if (colorClass === "red") {
    const isUnidirectional = feature.scaleType === "unidirectional"
    const markerPos = feature.markerPosition !== undefined ? feature.markerPosition : 50
    return isUnidirectional || markerPos >= 50 ? "Elevated" : "Low"
  }
  return "Elevated"
}

export function structuralSoundResultLabel(featureOrObserved, colorClass) {
  const observed =
    typeof featureOrObserved === "string"
      ? featureOrObserved
      : String(featureOrObserved?.structuralSoundLabel || featureOrObserved?.observed || "")
  const normalized = observed.toLowerCase()
  // Negated results first: "No significant additional …" contains the
  // positive phrase and must not read as energy observed.
  if (
    normalized.includes("no significant additional") ||
    normalized.includes("no additional") ||
    normalized.includes("no prominent")
  ) {
    return "No additional energy pattern detected"
  }
  if (normalized.includes("additional heart-sound energy") || normalized.includes("additional heart sound energy")) {
    return "Additional energy observed"
  }
  if (normalized.includes("prominent")) {
    if (normalized.includes("no prominent")) return "No prominent pattern"
    return "Prominent pattern"
  }
  if (normalized.includes("borderline")) return "Borderline repeatability"
  if (
    normalized.includes("repeatable structural") ||
    normalized.includes("repeatable pattern") ||
    normalized.includes("some repeated")
  )
    return "Repeatable pattern"
  if (normalized.includes("limited") || normalized.includes("unavailable") || normalized.includes("not assessed"))
    return "Unable to assess"
  if (colorClass === "red") return "Prominent pattern"
  if (colorClass === "amber") return "Borderline repeatability"
  return "No prominent pattern"
}
